import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card } from './ui/card';
import { Calculator, Bug, Leaf } from 'lucide-react';
import ServiceSelector from './ServiceSelector';
import RoofCalculatorPanel from './RoofCalculatorPanel';
import SidingCalculatorPanel from './SidingCalculatorPanel';
import GutterCalculatorPanel from './GutterCalculatorPanel';
import DrivewayCalculatorPanel from './DrivewayCalculatorPanel';
import DeckCalculatorPanel from './DeckCalculatorPanel';
import FenceCalculatorPanel from './FenceCalculatorPanel';
import FlatRateCalculatorPanel from './FlatRateCalculatorPanel';
import { useActiveServices } from '../hooks/useActiveServices';

const ServiceCalculatorDashboard = ({
  roofData, setRoofData, onCalculateRoof,
  measurementTotals: externalMeasurementTotals,
  calculations, setCalculations,
  // Tier 2 additions — all optional / backwards-compatible:
  panelRefs,             // { roof, siding, gutter, … } — useRef objects exposed via useImperativeHandle on each panel
  panelInputs = {},      // { svc: lastKnownInputs } — used to restore inputs on re-toggle
  onPanelInputsChange,   // (svc, inputs) => void — called by panels on every input change
  resetKey = 0,          // bump to force every panel to remount with fresh initialInputs
  taxRate = 0,           // P1: company tax rate from settings/billing (0 when disabled) — sole tax authority
}) => {
  // Services the tenant has turned on in Settings → App Configuration.
  // This is the single source of truth for which panels are allowed to render.
  const { activeServices, loading: servicesLoading } = useActiveServices();
  const activeSet = useMemo(() => new Set(activeServices), [activeServices]);

  // Track which services are selected. Start EMPTY — the old default of
  // ['roof'] caused Roof to render even when it was turned off in Settings,
  // because the panel gate checks `selectedServices.includes('roof')` and that
  // was true before the settings toggle was ever consulted.
  const [selectedServices, setSelectedServices] = useState([]);
  
  // Track which panels are expanded
  const [expandedPanels, setExpandedPanels] = useState({
    roof: false,
    siding: false,
    gutter: false,
    driveway: false,
    deck: false,
    fence: false,
    pest_control: false,
    lawn_care: false,
  });
  const [manualOverride, setManualOverride] = useState({
    roofSqFt: false,
    sidingSqFt: false,
    drivewaySqFt: false,
    deckSqFt: false,
    fenceFt: false,
    gutterFt: false,
  });

  // Use external measurement totals from parent (GoogleMapMeasurement via RoofEstimator)
  // Fallback to zeros if not provided
  const measurementTotals = externalMeasurementTotals || {
    lineFeet: 0,
    areaSqFt: 0,
    perimeterFt: 0,
  };

  const [serviceTotals, setServiceTotals] = useState({}); // kept for backwards compatibility, but footer totals now derive from global calculations

  const handleServiceTotalChange = (serviceId, totalValues) => {
    setServiceTotals((prev) => ({
      ...prev,
      [serviceId]: totalValues,
    }));

    // Also persist full input/result shape into top-level calculations state
    if (setCalculations) {
      setCalculations((prev) => ({
        ...(prev || {}),
        [serviceId]: totalValues || null,
      }));
    }
  };

  // ── Tier 2: panel input mirror ────────────────────────────────────────────
  // Each non-Roof panel pushes its current inputs up via `onInputsChange`
  // so the parent can (a) detect staleness against `calculations[svc].input`,
  // (b) restore inputs into a panel that was un-toggled then re-toggled.
  // Wired through `onPanelInputsChange` prop from RoofEstimator.
  const reportInputs = (svc, inputs) => {
    if (typeof onPanelInputsChange === 'function') {
      onPanelInputsChange(svc, inputs);
    }
  };

  // Use global multi-service calculations as the single source of truth
  const serviceOrder = ['roof', 'siding', 'gutter', 'driveway', 'deck', 'fence', 'pest_control', 'lawn_care'];

  const { sumSubtotal, sumTax, grandTotal } = (() => {
    if (!calculations) return { sumSubtotal: 0, sumTax: 0, grandTotal: 0 };

    let subtotal = 0;
    let tax = 0;

    serviceOrder.forEach((key) => {
      const svc = calculations[key];
      if (!svc || !svc.result) return;
      const grand = Number(svc.result.grandTotal) || 0;
      if (grand <= 0) return;

      subtotal += Number(svc.result.subtotal) || 0;
      tax += Number(svc.result.tax) || 0;
    });

    return {
      sumSubtotal: subtotal,
      sumTax: tax,
      grandTotal: subtotal + tax,
    };
  })();


  // Toggle service selection
  const handleServiceToggle = (serviceId) => {
    // Hard guard — never allow selection of a service the tenant has disabled.
    if (!activeSet.has(serviceId)) return;
    setSelectedServices((prev) => {
      if (prev.includes(serviceId)) {
        // Tier 2 — Removing a service from the chip selector must also null
        // its `calculations[serviceId]` entry, otherwise QuoteSummary keeps
        // showing a line item and Grand Total keeps including its cost.
        if (setCalculations) {
          setCalculations((cur) => {
            if (!cur || cur[serviceId] == null) return cur;
            return { ...cur, [serviceId]: null };
          });
        }
        return prev.filter((id) => id !== serviceId);
      } else {
        // Add service and auto-expand its panel
        setExpandedPanels((panels) => ({ ...panels, [serviceId]: true }));
        return [...prev, serviceId];
      }
    });
  };

  // Tier 2 — when the parent bumps `resetKey` (Clear Calculator), also
  // clear the locally-owned `selectedServices` so chips visually un-toggle
  // and panels collapse. We skip the initial 0 → 0 case via a ref guard.
  const lastResetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (resetKey !== lastResetKeyRef.current) {
      lastResetKeyRef.current = resetKey;
      setSelectedServices([]);
    }
  }, [resetKey]);

  // Reconcile `selectedServices` with the tenant's "Services Offered" setting.
  //   1. Drop any id that is no longer active (so panels stop rendering).
  //   2. If nothing is selected yet AND settings have loaded, auto-select the
  //      first active service to preserve the original one-tap quoting UX.
  //   3. Also clear any `calculations[id]` for services that are no longer
  //      active — otherwise a service toggled OFF mid-session could continue
  //      contributing to the Grand Total from stale state.
  useEffect(() => {
    if (servicesLoading) return;
    setSelectedServices((prev) => {
      const filtered = prev.filter((id) => activeSet.has(id));
      if (filtered.length === 0 && activeServices.length > 0) {
        const first = activeServices[0];
        setExpandedPanels((panels) => ({ ...panels, [first]: true }));
        return [first];
      }
      return filtered.length === prev.length ? prev : filtered;
    });
    if (setCalculations) {
      setCalculations((prev) => {
        if (!prev) return prev;
        let changed = false;
        const next = { ...prev };
        Object.keys(next).forEach((id) => {
          if (!activeSet.has(id) && next[id] != null) {
            next[id] = null;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [servicesLoading, activeServices, activeSet, setCalculations]);

  // Toggle panel expansion
  const handlePanelToggle = (serviceId) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Calculator className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold text-secondary">Service Calculations</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Select one or more services below to configure details and calculate pricing.
        </p>
      </div>

      {/* Service Selector */}
      <ServiceSelector
        selectedServices={selectedServices}
        onServiceToggle={handleServiceToggle}
      />

      {/* Dynamic Service Panels */}
      <div className="space-y-4">
        {activeSet.has('roof') && selectedServices.includes('roof') && (
          <RoofCalculatorPanel
            isOpen={expandedPanels.roof}
            onToggle={() => handlePanelToggle('roof')}
            roofData={roofData}
            setRoofData={setRoofData}
            onCalculate={onCalculateRoof}
            measurementTotals={measurementTotals}
            manualOverride={manualOverride}
            setManualOverride={setManualOverride}
            onServiceTotalChange={handleServiceTotalChange}
          />
        )}

        {activeSet.has('siding') && selectedServices.includes('siding') && (
          <SidingCalculatorPanel
            key={`siding-${resetKey}`}
            ref={panelRefs?.siding}
            isOpen={expandedPanels.siding}
            onToggle={() => handlePanelToggle('siding')}
            measurementTotals={measurementTotals}
            manualOverride={manualOverride}
            setManualOverride={setManualOverride}
            onServiceTotalChange={handleServiceTotalChange}
            initialInputs={panelInputs?.siding}
            onInputsChange={reportInputs}
          />
        )}

        {activeSet.has('gutter') && selectedServices.includes('gutter') && (
          <GutterCalculatorPanel
            key={`gutter-${resetKey}`}
            ref={panelRefs?.gutter}
            isOpen={expandedPanels.gutter}
            onToggle={() => handlePanelToggle('gutter')}
            measurementTotals={measurementTotals}
            manualOverride={manualOverride}
            setManualOverride={setManualOverride}
            onServiceTotalChange={handleServiceTotalChange}
            initialInputs={panelInputs?.gutter}
            onInputsChange={reportInputs}
          />
        )}

        {activeSet.has('driveway') && selectedServices.includes('driveway') && (
          <DrivewayCalculatorPanel
            key={`driveway-${resetKey}`}
            ref={panelRefs?.driveway}
            isOpen={expandedPanels.driveway}
            onToggle={() => handlePanelToggle('driveway')}
            measurementTotals={measurementTotals}
            manualOverride={manualOverride}
            setManualOverride={setManualOverride}
            onServiceTotalChange={handleServiceTotalChange}
            initialInputs={panelInputs?.driveway}
            onInputsChange={reportInputs}
          />
        )}

        {activeSet.has('deck') && selectedServices.includes('deck') && (
          <DeckCalculatorPanel
            key={`deck-${resetKey}`}
            ref={panelRefs?.deck}
            isOpen={expandedPanels.deck}
            onToggle={() => handlePanelToggle('deck')}
            measurementTotals={measurementTotals}
            manualOverride={manualOverride}
            setManualOverride={setManualOverride}
            onServiceTotalChange={handleServiceTotalChange}
            initialInputs={panelInputs?.deck}
            onInputsChange={reportInputs}
          />
        )}

        {activeSet.has('fence') && selectedServices.includes('fence') && (
          <FenceCalculatorPanel
            key={`fence-${resetKey}`}
            ref={panelRefs?.fence}
            isOpen={expandedPanels.fence}
            onToggle={() => handlePanelToggle('fence')}
            measurementTotals={measurementTotals}
            manualOverride={manualOverride}
            setManualOverride={setManualOverride}
            onServiceTotalChange={handleServiceTotalChange}
            initialInputs={panelInputs?.fence}
            onInputsChange={reportInputs}
          />
        )}

        {activeSet.has('pest_control') && selectedServices.includes('pest_control') && (
          <FlatRateCalculatorPanel
            serviceId="pest_control"
            serviceName="Pest Control"
            ServiceIcon={Bug}
            isOpen={expandedPanels.pest_control}
            onToggle={() => handlePanelToggle('pest_control')}
            onServiceTotalChange={handleServiceTotalChange}
            taxRate={taxRate}
          />
        )}

        {activeSet.has('lawn_care') && selectedServices.includes('lawn_care') && (
          <FlatRateCalculatorPanel
            serviceId="lawn_care"
            serviceName="Lawn Care"
            ServiceIcon={Leaf}
            isOpen={expandedPanels.lawn_care}
            onToggle={() => handlePanelToggle('lawn_care')}
            onServiceTotalChange={handleServiceTotalChange}
            taxRate={taxRate}
          />
        )}
      </div>

      {/* Grand Total */}
      <Card className="p-6 bg-secondary/10 border-2 border-secondary/30">
        <div className="flex justify-between items-center">
          <span className="text-lg font-bold text-secondary">Grand Total</span>
          <span className="text-3xl font-bold text-secondary">${grandTotal.toFixed(2)}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-right">
          Subtotal: ${sumSubtotal.toFixed(2)} | Tax: ${sumTax.toFixed(2)}
        </p>
      </Card>
    </div>
  );
};

export default ServiceCalculatorDashboard;
